// =============================================================================
// VoucherDetailScreen — one discount code / gift voucher: who bought it, what
// it is worth, where it was redeemed, and its full history (the voucher
// counterpart of BookingDetailScreen's "Geschiedenis").
// =============================================================================
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatError } from '../lib/errors';
import { formatVenueDateTime } from '../lib/time';
import {
  fetchVoucherDetail,
  voucherStatusOf,
  type VoucherDetail,
  type VoucherEvent,
  type VoucherPurchaseStatus,
} from '../lib/vouchers';
import { resendVoucherEmail } from '../api/resend-voucher-email';
import { AuditTimeline, formatAuditValue } from '../components/AuditTimeline';

export function VoucherDetailScreen() {
  const { code } = useParams<{ code: string }>();
  const [detail, setDetail] = useState<VoucherDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState<{
    kind: 'ok' | 'err';
    message: string;
  } | null>(null);

  const load = useCallback(async () => {
    if (!code) return;
    setError(null);
    try {
      const d = await fetchVoucherDetail(code);
      if (!d) {
        setError(`Voucher "${code}" niet gevonden.`);
        setDetail(null);
        return;
      }
      setDetail(d);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const handleResend = useCallback(async () => {
    if (!detail) return;
    setResendBusy(true);
    setResendNotice(null);
    const result = await resendVoucherEmail({ codeId: detail.code.id });
    setResendBusy(false);
    if (!result.ok || !result.data) {
      const msg = result.error?.message ?? result.error?.error ?? 'onbekende fout';
      setResendNotice({ kind: 'err', message: `Mislukt: ${msg}` });
      return;
    }
    setResendNotice({ kind: 'ok', message: 'Verstuurd ✓' });
    void load();
  }, [detail, load]);

  if (loading) return <div className="screen">Laden…</div>;
  if (error || !detail) {
    return (
      <div className="screen">
        <Link to="/vouchers" className="back-link">← Vouchers</Link>
        <div className="error-card">{error ?? 'Niet gevonden'}</div>
      </div>
    );
  }

  const { code: dc, purchase, redemptions, events } = detail;
  const status = voucherStatusOf(dc);
  const discountStr =
    dc.kind === 'percent'
      ? `${dc.percent_off}%`
      : `€ ${((dc.flat_off_cents ?? 0) / 100).toFixed(2)}`;
  const usage =
    dc.max_uses == null ? `${dc.uses_count}× / ∞` : `${dc.uses_count}× / ${dc.max_uses}×`;
  const isGiftPurchase = purchase != null;

  return (
    <div className="screen detail-screen voucher-detail-screen">
      <Link to="/vouchers" className="back-link">← Vouchers</Link>

      <div className="detail-header">
        <h1 className="voucher-code">{dc.code}</h1>
        <div className="detail-pills">
          <span className={`voucher-pill ${status.className}`}>{status.label}</span>
          {purchase ? <PurchasePill status={purchase.status} /> : null}
        </div>
      </div>

      <div className="detail-grid">
        <section>
          <h2>Voucher</h2>
          <dl>
            <dt>Korting</dt>
            <dd>
              {discountStr}
              {dc.max_party_size != null ? (
                <span className="muted"> · max. {dc.max_party_size} ticket{dc.max_party_size === 1 ? '' : 's'}</span>
              ) : null}
            </dd>
            <dt>Geldig voor</dt><dd>{dc.experience_name ?? <em>alle experiences</em>}</dd>
            <dt>Geldig van</dt><dd>{dc.valid_from ? formatVenueDateTime(dc.valid_from) : '—'}</dd>
            <dt>Geldig t/m</dt><dd>{dc.valid_until ? formatVenueDateTime(dc.valid_until) : '∞'}</dd>
            <dt>Gebruik</dt><dd>{usage}</dd>
            <dt>Aangemaakt</dt>
            <dd>
              {formatVenueDateTime(dc.created_at)}
              {dc.created_by_email ? <span className="muted"> · {dc.created_by_email}</span> : null}
            </dd>
            {dc.notes ? (<><dt>Notitie</dt><dd>{dc.notes}</dd></>) : null}
          </dl>
        </section>

        {purchase ? (
          <section>
            <h2>Koper</h2>
            <dl>
              <dt>Naam</dt><dd>{purchase.buyer_name ?? '—'}</dd>
              <dt>E-mail</dt>
              <dd><a href={`mailto:${purchase.buyer_email}`}>{purchase.buyer_email}</a></dd>
              <dt>Gekocht</dt><dd>{formatVenueDateTime(purchase.created_at)}</dd>
              <dt>Tickets</dt><dd>{purchase.ticket_count} × € {(purchase.unit_price_cents / 100).toFixed(2)}</dd>
              <dt>Betaald</dt><dd>€ {(purchase.amount_cents / 100).toFixed(2)}</dd>
              <dt>Taal</dt><dd>{purchase.locale ?? '—'}</dd>
              <dt>Bon gemaild</dt>
              <dd>{purchase.email_sent_at ? formatVenueDateTime(purchase.email_sent_at) : <span className="muted">nog niet</span>}</dd>
              {purchase.stripe_payment_intent_id ? (
                <><dt>Stripe</dt><dd><code className="voucher-pi">{purchase.stripe_payment_intent_id}</code></dd></>
              ) : null}
            </dl>
            {isGiftPurchase && purchase.status === 'paid' ? (
              <div className="detail-actions">
                <button
                  className="btn-link"
                  onClick={() => void handleResend()}
                  disabled={resendBusy}
                  title="Verstuur de cadeaubon-mail (code + geldigheid) opnieuw naar de koper"
                >
                  {resendBusy ? 'Versturen…' : 'Bon opnieuw mailen'}
                </button>
                {resendNotice ? (
                  <span
                    className={
                      resendNotice.kind === 'ok'
                        ? 'resend-notice resend-notice-ok'
                        : 'resend-notice resend-notice-err'
                    }
                  >
                    {resendNotice.message}
                  </span>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        <section>
          <h2>Inwisselingen</h2>
          {redemptions.length === 0 ? (
            <p className="muted">Nog niet ingewisseld.</p>
          ) : (
            <ul className="voucher-redemption-list">
              {redemptions.map((r) => (
                <li key={r.id}>
                  <div>
                    <strong>{formatVenueDateTime(r.redeemed_at)}</strong>
                    {' · '}€ {(r.amount_off_cents / 100).toFixed(2)} korting
                    {r.customer_name ? <> · {r.customer_name}</> : null}
                  </div>
                  <div className="voucher-redemption-departures">
                    {r.departures.length === 0 ? (
                      <span className="muted">{r.booking_number ?? r.booking_id}</span>
                    ) : (
                      r.departures.map((d) => (
                        <Link
                          key={d.departure_number}
                          to={`/booking/${d.departure_number}`}
                          className="voucher-code"
                        >
                          {d.departure_number}
                          <span className="muted"> · {formatVenueDateTime(d.departure_start)} · {d.lifecycle_status}</span>
                        </Link>
                      ))
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="audit-section">
        <h2>Geschiedenis</h2>
        <AuditTimeline
          items={events.map((ev) => ({
            id:          ev.id,
            created_at:  ev.created_at,
            actor_type:  ev.actor_type,
            actor_label: ev.actor_label,
            action:      ev.action,
            detail:      eventDetail(ev),
            derived:     ev.source === 'derived',
          }))}
        />
      </section>
    </div>
  );
}

function PurchasePill({ status }: { status: VoucherPurchaseStatus }) {
  const map: Record<VoucherPurchaseStatus, { label: string; className: string }> = {
    paid:     { label: 'Betaald',        className: 'voucher-status-ok' },
    refunded: { label: 'Terugbetaald',   className: 'voucher-status-warn' },
    failed:   { label: 'Betaling mislukt', className: 'voucher-status-off' },
    pending:  { label: 'In afwachting',  className: 'voucher-status-warn' },
  };
  const m = map[status] ?? { label: status, className: 'voucher-status-off' };
  return <span className={`voucher-pill ${m.className}`}>{m.label}</span>;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v != null && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function eur(cents: unknown): string | null {
  return typeof cents === 'number' ? `€ ${(cents / 100).toFixed(2)}` : null;
}

// One-line summary per action; falls back to the raw to_value JSON.
function eventDetail(ev: VoucherEvent): ReactNode {
  const to = asRecord(ev.to_value);
  const meta = asRecord(ev.metadata);
  switch (ev.action) {
    case 'voucher_purchase_started':
    case 'voucher_purchase_paid': {
      const parts = [
        typeof to.ticket_count === 'number' ? `${to.ticket_count} ticket${to.ticket_count === 1 ? '' : 's'}` : null,
        eur(to.amount_cents),
        typeof meta.payment_method_type === 'string' ? meta.payment_method_type : null,
      ].filter(Boolean);
      return parts.join(' · ');
    }
    case 'voucher_payment_failed':
    case 'voucher_purchase_refunded':
      return [eur(meta.amount_cents), typeof meta.stripe_payment_intent_id === 'string' ? meta.stripe_payment_intent_id : null]
        .filter(Boolean)
        .join(' · ');
    case 'voucher_email_sent':
      return typeof to.to === 'string' ? to.to : null;
    case 'discount_code_redeemed': {
      const nr = typeof to.booking_number === 'string' ? to.booking_number : null;
      return (
        <>
          {nr ?? '—'}
          {eur(to.amount_off_cents) ? <> · {eur(to.amount_off_cents)} korting</> : null}
        </>
      );
    }
    case 'admin_refund_voucher': {
      const nr = typeof meta.booking_number === 'string' ? meta.booking_number : null;
      return (
        <>
          {nr ? <>voor boeking {nr}</> : null}
          {eur(meta.amount_cents) ? <> · {eur(meta.amount_cents)}</> : null}
        </>
      );
    }
    case 'discount_code_marked_used':
    case 'discount_code_use_unmarked':
      return typeof to.uses_count === 'number' ? `gebruik: ${to.uses_count}×` : null;
    case 'discount_code_created':
    case 'discount_code_deactivated':
      return null;
    default:
      return formatAuditValue(ev.to_value);
  }
}
